/**
 * Indices barrel. Registers the built-in facades on a sheet.
 *
 * Consumers call `registerBuiltinFacades(sheet)` once after
 * constructing the sheet (before any row lands), typically from
 * `RawSheet.hydrate`. The function is idempotent within a sheet —
 * repeated calls throw from the underlying registry's dedupe.
 */

import type { ModelSheet } from "../sheet/rows-sheet.ts";
import { DataFacade } from "../facades/data-facade.ts";
import { EventsFacade, EVENT_SHAPE } from "../facades/events-facade.ts";
import { ListAggregateFacade } from "../facades/list-aggregate-facade.ts";
import { RegressionFacade } from "../facades/regression-facade.ts";
import { SchedulingLinkFacade } from "../facades/scheduling-link-facade.ts";
import { TasksFacade } from "../facades/tasks-facade.ts";
import { WeatherFacade, WEATHER_SHAPE } from "../facades/weather-facade.ts";
import { TASK_SHAPE } from "./tasks-index.ts";

export { DataIndex } from "./data-index.ts";
export type { DataIndexDiagnostics } from "./data-index.ts";
export {
  EventItemAllDay,
  EventItemTimed,
  EventsIndex,
  EVENT_SHAPE,
  eventValues,
  RecurrenceInfo,
  recurringMasterDtstartExcluded,
} from "./events-index.ts";
export type {
  EventItem,
  EventRef,
  EventsIndexDiagnostics,
} from "./events-index.ts";
export { EventPatch } from "./events/event-patch.ts";
export { EventTimeRange } from "./events/event-item.ts";
export { TasksIndex, TASK_SHAPE } from "./tasks-index.ts";
export type { TasksIndexDiagnostics } from "./tasks-index.ts";
export { TaskCalendarItem } from "./tasks-index-types.ts";
export { RegressionIndex } from "./regression-index.ts";
export type {
  KernelSmoothResult,
  RegressionAttributes,
  RegressionPoint,
} from "./regression-index.ts";

// Data-preset types — a client composes its own presets through
// `SheetRegistries.extend(…, { dataPresets })`; reads go through a sheet's
// `registries.dataPresets`.
export type { DataPresetSpec } from "../rows/data-preset-spec.ts";
export {
  propsFromNodes,
  type GraphSpecNode,
} from "./data/graph-props.ts";
export type { CellAdapter, Graph, GroupedCellAdapter } from "../stores/graph-types.ts";
export { extractSeries } from "./data/series.ts";
export type { TimepointCellEntry, SeriesResult } from "./data/series.ts";

/**
 * Register every built-in shape + facade on `sheet`. Safe to call
 * once per sheet; re-registration throws.
 */
export function registerBuiltinFacades(sheet: ModelSheet): void {
  // Shapes — registered so consumers can probe through
  // `sheet.shapes.get(key)` without a facade.
  sheet.shapes.register(TASK_SHAPE);
  sheet.shapes.register(EVENT_SHAPE);
  sheet.shapes.register(WEATHER_SHAPE);

  // Facade factories — resolved lazily per row on first
  // `row.as(KEY)` lookup; results cached.
  sheet.facades.register(TasksFacade.factory);
  sheet.facades.register(EventsFacade.factory);
  sheet.facades.register(ListAggregateFacade.factory);
  sheet.facades.register(WeatherFacade.factory);
  sheet.facades.register(SchedulingLinkFacade.factory);
  sheet.facades.register(DataFacade.factory);
  sheet.facades.register(RegressionFacade.factory);
}
